import { Card, makeStyles, Typography } from '@material-ui/core'
import type { Theme } from '@material-ui/core/styles'
import DeleteIcon from '@material-ui/icons/Delete'
import EditIcon from '@material-ui/icons/Edit'
import CopyIcon from '@material-ui/icons/FileCopy'
import ImportExportIcon from '@material-ui/icons/ImportExport'
import type { FollowUp } from '@paintscout/api'
import { getFeatures, getFollowUpScheduleInfo, getObjectLabels } from '@paintscout/util/builder'
import React, { useState } from 'react'
import get from 'lodash/get'
import classnames from 'classnames'
import type { QuoteDocument } from 'paintscout'
import ErrorIcon from '@material-ui/icons/Error'
import striptags from 'striptags'
import ActionButton from '@paintscout/ui/components/ActionButton'
import HtmlContent from '@paintscout/ui/components/HtmlContent'
import Table, { TableBody, TableCell, TableRow } from '@paintscout/ui/components/Table'
import Button from '@paintscout/ui/components/Button'
import AddItemButton from '@paintscout/ui/components/AddItemButton'
import { CheckIcon } from '@paintscout/ui/icons'
import Hidden from '@paintscout/ui/components/Hidden'
import { Grid, Switch } from '@paintscout/ui'
import FollowUpSummary from '../FollowUpSummary'
import { useClientOptions } from '../../providers/ClientOptionsProvider'
import DropdownSelect from '../DropdownSelect'

const JUSTIFY_SPACE_BETWEEN = 'space-between'

const formatPlaceholderLabel = (key: string): string =>
  !key
    ? ''
    : key
        .replace(/([A-Z])/g, ' $1')
        .replace(/^./, (str) => str.toUpperCase())
        .trim()

const convertPlaceholdersToSpans = (html: string): string => {
  if (!html) return html
  return html.replace(
    /\{\{(\w+)\}\}/g,
    (_, key) =>
      `<span class="template-placeholder" data-key="${key}" contenteditable="false">${formatPlaceholderLabel(
        key
      )}</span>`
  )
}

const useStyles = makeStyles<Theme, FollowUpTableProps>(
  (theme) => ({
    root: {
      '& .template-placeholder': {
        paddingLeft: theme.size['0.5'],
        paddingRight: theme.size['0.5'],
        paddingTop: theme.size['0.25'],
        paddingBottom: theme.size['0.25'],
        marginTop: theme.spacing(0.25),
        marginBottom: theme.spacing(0.25),
        marginLeft: theme.spacing(0.125),
        marginRight: theme.spacing(0.125),
        fontWeight: theme.typography.fontWeightMedium,
        borderRadius: theme.borderRadius.sm,
        border: `1px solid ${theme.palette.common.black}`,
        display: 'inline-block',
        verticalAlign: 'baseline'
      }
    },
    showAdditionalButton: {
      padding: 0,
      justifyContent: 'flex-start',
      paddingLeft: theme.spacing(0.5),
      width: 0
    },
    cellButton: {
      width: '100%',
      display: 'contents',
      // keep focus outline within the table
      cursor: 'pointer'
    },
    checkIcon: {
      fontSize: '15px',
      margin: '2px 5px',
      ['@media (max-width:420px)']: {
        marginLeft: 0
      }
    },
    reviewText: {
      color: theme.palette.error.main
    },
    errorIcon: {
      color: theme.palette.error.main,
      fontSize: '20px',
      margin: '0px 3px',
      ['@media (max-width:420px)']: {
        marginLeft: 0
      }
    },
    disabled: {
      color: 'grey'
    },
    addButton: {
      marginTop: theme.spacing(1)
    },
    tableRow: {
      border: `none`
    },
    border: {
      border: `1px solid ${theme.palette.divider}`,
      borderRadius: theme.borderRadius.md,
      boxShadow: 'none',
      padding: theme.spacing(1, 2)
    },
    tableHeader: {
      marginBottom: theme.spacing(3)
    },
    dividerRow: {
      borderBottom: `2px solid ${theme.palette.grey}`
    },
    cellHeader: {
      display: 'flex',
      justifyContent: JUSTIFY_SPACE_BETWEEN,
      alignItems: 'center'
    },
    cellBody: {
      display: 'flex',
      flexDirection: 'row',
      justifyContent: JUSTIFY_SPACE_BETWEEN,
      alignItems: 'end',
      paddingTop: 0,
      [theme.breakpoints.down('lg')]: {
        flexDirection: 'column',
        alignItems: 'start'
      }
    },
    cellDetails: {
      display: 'flex',
      flexDirection: 'row',
      justifyContent: JUSTIFY_SPACE_BETWEEN,
      paddingBottom: '3px',

      [theme.breakpoints.up('xl')]: {
        marginLeft: theme.spacing(1)
      },
      [theme.breakpoints.down('lg')]: {
        paddingLeft: '1.5px'
      }
    },
    emailDetails: {
      marginRight: '20px'
    },
    smsDetails: {
      ['@media (max-width:420px)']: {
        marginLeft: '20px'
      }
    },
    followUpsBody: {
      [theme.breakpoints.down('xs')]: {
        paddingLeft: 0
      }
    },
    followUpStatus: {
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'end',
      justifyContent: JUSTIFY_SPACE_BETWEEN,
      paddingBottom: '3px',
      ['@media (max-width:420px)']: {
        flexDirection: 'column',
        alignItems: 'start'
      }
    },
    followUpSummary: {
      marginTop: '10px',
      [theme.breakpoints.down('xs')]: {
        marginBottom: '10px'
      }
    },
    smallActionButton: {
      display: 'flex',
      flexDirection: 'row',
      justifyContent: 'flex-end',
      height: '30px'
    },
    switch: {
      marginBottom: 0,
      ['@media (max-width:420px)']: {
        marginLeft: 0
      }
    },
    notEnabledText: {
      color: theme.paintscout.palette.brand.Neutral.P60,
      marginLeft: theme.spacing(0.5)
    },
    subjectContent: {
      '& .template-placeholder': {
        paddingLeft: theme.size['1'],
        paddingRight: theme.size['1'],
        paddingTop: theme.size['0.25'],
        paddingBottom: theme.size['0.25'],
        marginTop: theme.spacing(0.25),
        marginBottom: theme.spacing(0.25),
        marginLeft: theme.spacing(0.125),
        marginRight: theme.spacing(0.125),
        fontWeight: theme.typography.fontWeightMedium,
        borderRadius: theme.borderRadius.md,
        border: `1px solid ${theme.palette.common.black}`,
        display: 'inline-block',
        verticalAlign: 'baseline'
      }
    }
  }),
  { name: 'FollowUpTable' }
)

export interface FollowUpTableProps {
  followUps: FollowUp[]
  controls?: {
    toggle?: boolean
    edit?: boolean
    copy?: boolean
    delete?: boolean
  }
  disabled?: boolean
  onAction?: (
    action: 'edit' | 'copy' | 'delete' | 'toggleSendEmail' | 'toggleSendText' | 'export',
    followUp: FollowUp
  ) => any
  isSuperadmin?: boolean
  settingsView?: boolean
  onAdd?: () => void
  quoteView?: boolean
  quote?: QuoteDocument
}

export default function FollowUpTable({ ...props }: FollowUpTableProps) {
  const classes = useStyles(props)
  const {
    followUps,
    controls = { edit: true, toggle: true, copy: true, delete: true },
    onAction,
    onAdd,
    disabled,
    isSuperadmin,
    quoteView,
    quote
  } = props
  const { options } = useClientOptions()
  const features = getFeatures({ options })
  const showSendText = get(features, 'quotes.followUps.sendText', false)
  const quoteLabel = getObjectLabels({ options }).quote
  const invoiceLabel = getObjectLabels({ options, invoice: true }).quote
  const hasControls = controls?.edit || controls?.copy || controls?.delete
  const [showAdditional, setShowAdditional] = useState([])
  const filterOptions = [
    { label: 'All Document Types', value: 'all' },
    { label: `${quoteLabel.plural}`, value: 'estimate' },
    { label: `${invoiceLabel.plural}`, value: 'invoice' }
  ]
  const [filterValue, setFilterValue] = useState(filterOptions[0])

  const actions = [
    {
      key: 'edit',
      label: 'Edit',
      icon: EditIcon
    },
    {
      key: 'copy',
      label: 'Copy',
      icon: CopyIcon
    },
    {
      key: 'delete',
      label: 'Delete',
      icon: DeleteIcon
    }
  ]
  if (isSuperadmin) {
    actions.push({
      key: 'export',
      label: 'Export',
      icon: ImportExportIcon
    })
  }

  const filteredFollowUps = followUps.filter((followUp) => {
    if (filterValue.value === 'all') {
      return true
    } else if (filterValue.value === 'estimate') {
      return !followUp.isInvoice
    } else if (filterValue.value === 'invoice') {
      return followUp.isInvoice
    }
  })

  return (
    <>
      <Table>
        {!quoteView && (
          <Grid container justifyContent="flex-start" className={classes.tableHeader}>
            <Grid item xs={12} sm={6} md={4}>
              <DropdownSelect
                variant="single"
                hideCloseButton
                options={filterOptions}
                value={filterValue}
                onChange={(option) => {
                  setFilterValue(option)
                }}
              />
            </Grid>
          </Grid>
        )}
        <TableBody>
          <TableRow className={classes.dividerRow} />
        </TableBody>
        <br />
        <TableBody className={classes.followUpsBody}>
          {filteredFollowUps.map((followUp, i) => {
            const smsMessageLength = followUp.smsMessage?.length || 0
            const cleanSmsLength = striptags(followUp?.smsMessage ?? '').length || 0
            const emailMessageLength = followUp.message?.length || 0
            const overMaxTextLength = smsMessageLength > 1500
            // Shared with the sidebar follow-up count and the get_follow_ups LLM tool so the
            // "Scheduled" vs "Not applicable to send" determination can't diverge (#20841).
            const { hasBeenSent, notStatusHappened, fromStatusHasHappened, overdueFollowUp, dueDate } =
              getFollowUpScheduleInfo({ quote, followUp })

            const followUpDetails = {
              hasBeenSent,
              notStatusHappened,
              fromStatusHasHappened,
              overdueFollowUp,
              dueDate
            }

            return (
              <>
                {/* Large view */}
                <TableRow
                  key={followUp.key}
                  classes={{
                    root: classnames({
                      [classes.tableRow]: true,
                      [classes.disabled]: overdueFollowUp
                    })
                  }}
                >
                  <Card className={classes.border}>
                    <TableCell className={classes.cellButton}>
                      <div onClick={() => controls?.edit && !disabled && onAction?.('edit', followUp)}>
                        <Hidden smUp>
                          <div className={classes.smallActionButton}>
                            {hasControls && (
                              <ActionButton
                                style={{ margin: '0px', padding: 0 }}
                                actions={actions}
                                onActionClick={(ev, action) => {
                                  onAction(action as any, followUp)
                                }}
                              />
                            )}
                          </div>
                        </Hidden>
                        <div className={classes.cellHeader}>
                          <Typography variant="body1" noWrap gutterBottom style={{ fontWeight: 500 }}>
                            <div className={classes.subjectContent}>
                              <HtmlContent content={convertPlaceholdersToSpans(followUp.subject)} />
                            </div>
                          </Typography>
                          {hasControls && (
                            <Hidden xsDown>
                              <ActionButton
                                actions={actions}
                                onActionClick={(ev, action) => {
                                  onAction(action as any, followUp)
                                }}
                              />
                            </Hidden>
                          )}
                        </div>
                        <div className={classes.cellBody}>
                          <FollowUpSummary
                            className={classes.followUpSummary}
                            followUp={followUp}
                            mt={2}
                            quote={quote}
                            followUpDetails={followUpDetails}
                          />
                          <div style={{ display: 'flex', flexDirection: 'column' }}>
                            <div className={classes.cellDetails}>
                              <div className={classnames(classes.emailDetails, classes.followUpStatus)}>
                                <Typography variant="body1" style={{ fontWeight: 450 }}>
                                  Email:
                                </Typography>
                                {quoteView && (
                                  <Switch
                                    classes={{ root: classes.switch }}
                                    checked={emailMessageLength === 0 ? false : followUp.sendEmail}
                                    disabled={emailMessageLength === 0 || !controls.toggle || overdueFollowUp}
                                    readOnly={disabled}
                                    data-testid={`toggle-follow-up-${followUp.key}`}
                                    onClick={() => {
                                      onAction?.('toggleSendEmail', followUp)
                                    }}
                                  />
                                )}
                                {!quoteView &&
                                  (followUp?.sendEmail ? (
                                    <div style={{ display: 'flex', flexDirection: 'row', color: 'green' }}>
                                      <CheckIcon className={classes.checkIcon} />
                                      <Typography variant="subtitle1" style={{ color: 'green' }}>
                                        Enabled
                                      </Typography>
                                    </div>
                                  ) : (
                                    <Typography variant="subtitle1" className={classes.notEnabledText}>
                                      Not Enabled
                                    </Typography>
                                  ))}
                              </div>
                              {showSendText && (
                                <div className={classnames(classes.smsDetails, classes.followUpStatus)}>
                                  <>
                                    <Typography variant="body1" style={{ fontWeight: 450, minWidth: '75px' }}>
                                      SMS Text:
                                    </Typography>
                                    {quoteView && (
                                      <Switch
                                        classes={{ root: classes.switch }}
                                        checked={
                                          overMaxTextLength || cleanSmsLength === 0 || !quote?.contact?.phone_number
                                            ? false
                                            : followUp.sendText
                                        }
                                        disabled={
                                          !controls.toggle ||
                                          overMaxTextLength ||
                                          cleanSmsLength === 0 ||
                                          overdueFollowUp ||
                                          !quote?.contact?.phone_number
                                        }
                                        readOnly={disabled}
                                        data-testid={`toggle-follow-up-send-text-${followUp.key}`}
                                        onClick={() => {
                                          onAction?.('toggleSendText', followUp)
                                        }}
                                      />
                                    )}
                                    {!quoteView &&
                                      (followUp.sendText ? (
                                        striptags(followUp?.smsMessage ?? '').length > 0 &&
                                        striptags(followUp?.smsMessage ?? '').length < 95 ? (
                                          <div style={{ display: 'flex', flexDirection: 'row', color: 'green' }}>
                                            <CheckIcon className={classes.checkIcon} />
                                            <Typography variant="subtitle1" style={{ color: 'green' }}>
                                              Enabled
                                            </Typography>
                                          </div>
                                        ) : (
                                          <div style={{ display: 'flex', flexDirection: 'row' }}>
                                            <ErrorIcon className={classes.errorIcon} />
                                            <Typography variant="subtitle1" className={classes.reviewText}>
                                              Error
                                            </Typography>
                                          </div>
                                        )
                                      ) : (
                                        <Typography variant="subtitle1" className={classes.notEnabledText}>
                                          Not Enabled
                                        </Typography>
                                      ))}
                                  </>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                        {isSuperadmin && (
                          <>
                            <Button
                              className={classes.showAdditionalButton}
                              variant={'text'}
                              onClick={(ev) => {
                                ev.stopPropagation()
                                if (showAdditional.includes(followUp.key)) {
                                  setShowAdditional((showAdditional) =>
                                    showAdditional.filter((key) => key !== followUp.key)
                                  )
                                } else {
                                  setShowAdditional((showAdditional) => [...showAdditional, followUp.key])
                                }
                              }}
                            >
                              <Typography variant="subtitle1" className={classes.extraInfo}>
                                {showAdditional.includes(followUp.key) ? 'x' : '+'}
                              </Typography>
                            </Button>
                            {showAdditional.includes(followUp.key) && (
                              <>
                                <Typography variant="subtitle2" className={classes.extraInfo}>
                                  {followUp?.created
                                    ? `Created: ${new Date(followUp?.created).toLocaleDateString()} - ${new Date(
                                        followUp?.created
                                      ).toTimeString()}`
                                    : ''}
                                </Typography>
                                <Typography variant="subtitle2" className={classes.extraInfo}>
                                  {followUp?.toggled &&
                                    `Toggled: ${new Date(followUp?.toggled).toLocaleDateString()} - ${new Date(
                                      followUp?.toggled
                                    ).toTimeString()}`}
                                </Typography>
                              </>
                            )}
                          </>
                        )}
                      </div>
                    </TableCell>
                  </Card>
                </TableRow>
                {i !== followUps.length - 1 && <div style={{ margin: '20px' }} />}
              </>
            )
          })}
        </TableBody>
      </Table>
      {!!onAdd && (
        <AddItemButton className={classes.addButton} onClick={onAdd} disabled={disabled}>
          New Follow-Up
        </AddItemButton>
      )}
    </>
  )
}
